import { useEffect, useState } from 'react';
import { useMetaTags } from './useMetaTags';
import {
  getMetaTagsFromSanity,
  formatForUseMetaTags,
} from '../api-sanity/metaTags';

/**
 * Custom hook that fetches meta tags from Sanity and applies them
 * Falls back to defaults if no Sanity data found
 *
 * @param {string} pageSlug - Page identifier (e.g., "home", "about", "services")
 * @param {boolean} skipFetch - Skip fetching from Sanity and use local defaults
 *
 * @example
 * // Fetch from Sanity and apply
 * useSanityMetaTags('home');
 *
 * @example
 * // With local overrides
 * const [metaTags, setMetaTags] = useState(null);
 * useSanityMetaTags('about', false);
 */
export const useSanityMetaTags = (pageSlug, skipFetch = false) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaTags, setMetaTags] = useState({});

  // Apply the fetched meta tags
  useMetaTags(metaTags);

  useEffect(() => {
    const fetchAndApply = async () => {
      try {
        setLoading(true);

        if (skipFetch) {
          setLoading(false);
          return;
        }

        // Fetch from Sanity
        const fetchedMetaTags = await getMetaTagsFromSanity(pageSlug);

        // Format and update state
        const formattedTags = formatForUseMetaTags(fetchedMetaTags);
        setMetaTags(formattedTags);

        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    fetchAndApply();
  }, [pageSlug, skipFetch]);

  return { loading, error };
};

export default useSanityMetaTags;
